import { Injectable, Injector } from '@angular/core';
import { AngularFireRemoteConfig } from '@angular/fire/remote-config';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { SettingsService } from '@toro/common/shared/services/settings/settings.service';

import {
  DEFAULT_FEATURE_FLAGS,
  FeatureFlags,
  getFeatureFlagRemoteKeys,
} from './feature-flags.interface';

/**
 * Serviço central de feature flags do app logged-home.
 *
 * Envolve `AngularFireRemoteConfig` para expor uma API tipada, reativa e
 * segura por padrão:
 *
 *  - `initialize()` — chamar via `APP_INITIALIZER` para garantir que o
 *    primeiro `fetchAndActivate` aconteça antes do primeiro consumo.
 *  - `isEnabled(key)` — snapshot síncrono, útil dentro de handlers.
 *  - `observe(key)` — Observable reativo, ideal para `async` pipe.
 *  - `getAll()` — snapshot completo (útil em debug).
 *
 * Se qualquer erro ocorrer (Firebase indisponível, chave ausente, etc.), o
 * serviço mantém os valores de `DEFAULT_FEATURE_FLAGS`, garantindo que a
 * aplicação nunca fique em estado inconsistente por conta das flags.
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagsService {
  private readonly flags$ = new BehaviorSubject<FeatureFlags>(DEFAULT_FEATURE_FLAGS);
  private initializePromise: Promise<void> | null = null;
  private remoteConfigInstance: AngularFireRemoteConfig | null = null;

  constructor(
    private injector: Injector,
    private settingsService: SettingsService,
  ) {}

  /**
   * Resolve `AngularFireRemoteConfig` de forma preguiçosa (via `Injector`),
   * evitando que a cadeia de DI force a resolução de `FIREBASE_OPTIONS`
   * antes do `SettingsService.loadEnvironmentSettings()` completar.
   */
  private getRemoteConfig(): AngularFireRemoteConfig {
    if (!this.remoteConfigInstance) {
      this.remoteConfigInstance = this.injector.get(AngularFireRemoteConfig);
    }
    return this.remoteConfigInstance;
  }

  /**
   * Inicializa o Remote Config e carrega os valores atuais das flags.
   * Idempotente: chamadas repetidas retornam a mesma Promise.
   */
  public initialize(): Promise<void> {
    if (this.initializePromise) {
      return this.initializePromise;
    }
    const remoteConfig = this.getRemoteConfig();
    this.initializePromise = remoteConfig
      .ensureInitialized()
      .then(() => remoteConfig.fetchAndActivate())
      .then(() => this.loadFlags())
      .then(() => undefined)
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('[FeatureFlagsService] Falha ao inicializar Remote Config:', error);
        // Mantém DEFAULT_FEATURE_FLAGS já emitidos pelo BehaviorSubject.
        this.initializePromise = null;
      });
    return this.initializePromise;
  }

  /**
   * Retorna o valor atual (snapshot) de uma flag específica.
   */
  public isEnabled(key: keyof FeatureFlags): boolean {
    return this.flags$.value[key];
  }

  /**
   * Observa o valor de uma flag, emitindo apenas quando ele muda.
   */
  public observe(key: keyof FeatureFlags): Observable<boolean> {
    return this.flags$.pipe(
      map((flags) => flags[key]),
      distinctUntilChanged(),
    );
  }

  /**
   * Retorna um Observable com o snapshot completo das flags.
   */
  public getFlags(): Observable<FeatureFlags> {
    return this.flags$.asObservable();
  }

  /**
   * Retorna o snapshot completo das flags (útil para debug).
   */
  public getAll(): FeatureFlags {
    return { ...this.flags$.value };
  }

  /**
   * Força uma nova busca no Remote Config. Útil para refresh manual em
   * cenários específicos (ex.: pull-to-refresh, mudança de contexto).
   */
  public async refresh(): Promise<void> {
    try {
      await this.getRemoteConfig().fetchAndActivate();
      await this.loadFlags();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[FeatureFlagsService] Falha ao atualizar Remote Config:', error);
    }
  }

  /**
   * Lê cada chave remota resolvida para o ambiente atual.
   *
   * O sufixo (`` para `production`, `_hml` para demais) é aplicado em runtime
   * via `getFeatureFlagRemoteKeys`, refletindo a convenção do Remote Config
   */
  private async loadFlags(): Promise<void> {
    try {
      const remoteConfig = this.getRemoteConfig();
      const remoteKeys = getFeatureFlagRemoteKeys(this.settingsService.getEnvironment());
      const next: FeatureFlags = { ...DEFAULT_FEATURE_FLAGS };
      const flagKeys = Object.keys(remoteKeys) as Array<keyof FeatureFlags>;
      await Promise.all(
        flagKeys.map(async (flagKey) => {
          const remoteKey = remoteKeys[flagKey];
          next[flagKey] = await remoteConfig.getBoolean(remoteKey);
        }),
      );
      this.flags$.next(next);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[FeatureFlagsService] Falha ao ler flags:', error);
    }
  }
}
